import React, {ReactNode} from 'react';
import './Drawer.scss';
import {reduxClearAll} from 'appredux/actions';
import {lshREMOVEAUTH} from 'services/LocalStorageService';
import NavigationService from 'services/NavigationService';
import {
  Box,
  Card,
  Divider,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Button,
  withWidth,
} from '@material-ui/core';
import {UpdatePasswordDialog} from 'components/UpdatePasswordDialog/UpdatePasswordDialog';
import {PERSISTOR} from 'appredux/index';
import {PermissionsService} from 'services/PermissionsService';
import BrowserNotificationService from 'services/BrowserNotificationService';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';
import {ISupplierUserProfile} from 'constants/interfaces';
import {connect} from 'react-redux';
import {NavLink} from 'react-router-dom';
import smallLogo from 'assets/images/logo.png';

// Font Awesome icons - Regular
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faBars,
  faBell,
  faPenToSquare,
  faSignOutAlt,
  faChartLine,
  faUser,
  faStore,
  faHouse,
  faTruck,
  faBuilding,
  faMoneyBillWave,
  faFile,
  faCubes,
  faInboxIn,
  faBarsProgress,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';

// **Import the SVG logo** directly
import KhulaLogo from 'assets/images/Logo.svg';

type DrawerProps = {
  user: ISupplierUserProfile;
  isDrawerOpen: boolean;
  onDrawerClose: VoidFunction;
  width: Breakpoint;
  /**
   * If true, we'll hide user info labels (name, depot, etc.)
   * and drawer text labels, but keep the avatar visible.
   */
  isMinimized?: boolean;
};

class Drawer extends React.Component<DrawerProps> {
  state = {
    userMenuAnchorElement: null as HTMLElement | null,
    updatePasswordDialogOpen: false,
  };

  logout() {
    reduxClearAll();
    lshREMOVEAUTH();
    PERSISTOR.purge();
    BrowserNotificationService.setCount(0);
    NavigationService.navigate('/login');
  }

  handleUpdatePassword() {
    this.setState({updatePasswordDialogOpen: true});
    this.handleMenuClose();
  }

  handleLogOut() {
    this.handleMenuClose();
    this.logout();
  }

  handleMenuOpen(event: React.MouseEvent<HTMLElement>) {
    this.setState({userMenuAnchorElement: event.currentTarget});
  }

  handleMenuClose() {
    this.setState({userMenuAnchorElement: null});
  }

  getDepotName(user: ISupplierUserProfile) {
    if (user.isKhulaAdmin) return 'Khula Admin';
    if (user.isAdmin) return 'All Depots';
    if (user?.depots?.length && user.depots?.length > 1) {
      return 'Multiple Depots';
    }
    return user.depot?.name + ' Depot';
  }

  showIntercom() {
    if ((window as any).Intercom) {
      (window as any).Intercom('show');
    }
  }

  render() {
    const {user, width, isDrawerOpen, onDrawerClose, isMinimized} = this.props;
    const userFullName = `${user.name} ${user.surname}`;
    const permissionsService = new PermissionsService(user);
    const isDrawerHidden = width === 'xs' || width === 'sm';

    // Conditionally add a class if minimized
    const drawerClassName = `drawer ${isMinimized ? 'drawer-minimized' : ''}`;

    return (
      <MuiDrawer
        className={drawerClassName}
        variant={isDrawerHidden ? 'temporary' : 'permanent'}
        anchor="left"
        open={isDrawerOpen}
        onClose={onDrawerClose}
      >
        <Box className="drawer-container">
          {/* TOP USER CARD area remains, but we hide name/depot if minimized */}
          <Box className="user-card-container">
            <Card className="user-card" variant="outlined">
              <Avatar
                alt={userFullName}
                src={user.pictureUrl}
                className="avatar"
              />

              {!isMinimized && (
                <>
                  <Typography className="user-name">{userFullName}</Typography>
                  <Typography className="user-depot">
                    {this.getDepotName(user)}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={(e) => this.handleMenuOpen(e)}
                    className="user-menu-button"
                  >
                    <FontAwesomeIcon icon={faBars} size="sm" />
                  </IconButton>
                </>
              )}
            </Card>

            {/* User menu popover */}
            <Menu
              anchorEl={this.state.userMenuAnchorElement}
              keepMounted
              anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
              transformOrigin={{vertical: 'top', horizontal: 'center'}}
              open={Boolean(this.state.userMenuAnchorElement)}
              onClose={() => this.handleMenuClose()}
            >
              <MenuItem
                onClick={() => {
                  this.handleMenuClose();
                  console.log('TODO: show notifications');
                }}
              >
                <FontAwesomeIcon icon={faBell} style={{marginRight: 8}} />
                Notifications
              </MenuItem>
              <MenuItem onClick={() => this.handleUpdatePassword()}>
                <FontAwesomeIcon
                  icon={faPenToSquare}
                  style={{marginRight: 8}}
                />
                Edit Profile
              </MenuItem>
              <MenuItem onClick={() => this.handleLogOut()}>
                <FontAwesomeIcon icon={faSignOutAlt} style={{marginRight: 8}} />
                Logout
              </MenuItem>
            </Menu>
          </Box>

          <UpdatePasswordDialog
            user={user}
            open={this.state.updatePasswordDialogOpen}
            onClose={() => this.setState({updatePasswordDialogOpen: false})}
          />

          {/* We only show the divider if there's text below */}
          {!isMinimized && <Divider />}

          {/* NAV LIST */}
          <List className="drawer-list">
            <DrawerListItem
              to="/"
              title="Overview"
              icon={<FontAwesomeIcon icon={faHouse} />}
            />
            <DrawerListItem
              to="/analytics"
              title="Analytics"
              icon={<FontAwesomeIcon icon={faChartLine} />}
              hidden={!permissionsService.shouldShowAnalytics()}
            />

            {/*
              notExact => if the route is /customers/...
              the link remains active, so the "Farmers" icon is
              highlighted on sub-routes like /customers/dashboard
            */}
            <DrawerListItem
              to="/applications"
              title="Applications"
              icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
              hidden={
                !(
                  permissionsService.shouldShowFundingApplications() ||
                  permissionsService.shouldShowFundingApplicationsViewOnly()
                )
              }
            />

            <DrawerListItem
              to="/customers"
              title="Farmers"
              notExact
              icon={<FontAwesomeIcon icon={faUser} />}
              hidden={!permissionsService.shouldShowCustomers()}
            />

            <DrawerListItem
              to="/groups"
              title="Groups"
              notExact
              icon={<FontAwesomeIcon icon={faUsers} />}
              hidden={!permissionsService.shouldShowGroups()}
            />

            {!isMinimized && <Divider style={{margin: '8px 0'}} />}

            <DrawerListItem
              to="/programs"
              title="Programs"
              notExact
              icon={<FontAwesomeIcon icon={faBarsProgress} />}
              hidden={
                !(
                  permissionsService.shouldShowPrograms() ||
                  permissionsService.shouldShowProgramsViewOnly()
                )
              }
            />

            <DrawerListItem
              to="/quotes"
              title="Quotes"
              icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
              hidden={!permissionsService.shouldShowQuotes()}
            />

            <DrawerListItem
              to="/orders"
              title="Orders"
              icon={<FontAwesomeIcon icon={faTruck} />}
              hidden={!permissionsService.shouldShowOrders()}
            />
            {!isMinimized && <Divider style={{margin: '8px 0'}} />}

            <DrawerListItem
              to="/inventory"
              title="Inventory"
              icon={<FontAwesomeIcon icon={faHouse} />}
              hidden={!permissionsService.shouldShowInventory()}
            />
            <DrawerListItem
              to="/products"
              title="Products"
              icon={<FontAwesomeIcon icon={faStore} />}
              hidden={!permissionsService.shouldShowProducts()}
            />
            <DrawerListItem
              to="/company"
              title="Company"
              notExact
              icon={<FontAwesomeIcon icon={faBuilding} />}
              hidden={!permissionsService.shouldShowCompany()}
            />

            {!isMinimized && <Divider style={{margin: '8px 0'}} />}
            <DrawerListItem
              to="/purchase-orders"
              title="Purchase Orders"
              icon={<FontAwesomeIcon icon={faFile} />}
              hidden={!permissionsService.shouldShowPurchaseOrders()}
            />
            <DrawerListItem
              to="/order-bundles"
              title="Order Bundles"
              notExact
              icon={<FontAwesomeIcon icon={faCubes} />}
              hidden={!permissionsService.shouldShowOrderBundles()}
            />
            <DrawerListItem
              to="/onboard"
              title="Onboarding"
              notExact
              icon={<FontAwesomeIcon icon={faInboxIn} />}
              hidden={!permissionsService.shouldShowOnboard()}
            />
          </List>

          <Box flex={1} />

          {isMinimized && (
            <div className="mini-logo-footer">
              <img src={smallLogo} alt="Logo" />
            </div>
          )}

          {/* Keep the contact card at the bottom hidden if minimized */}
          {!isMinimized && (
            <>
              <Divider />
              <Box className="contact-us-container">
                <Card className="contact-us-card" variant="outlined">
                  <Box className="contact-us-content">
                    <img src={KhulaLogo} alt="Khula!" className="khula-logo" />
                    <Button
                      variant="text"
                      className="contact-us-button"
                      onClick={() => this.showIntercom()}
                    >
                      Contact us
                    </Button>
                  </Box>
                </Card>
              </Box>
            </>
          )}
        </Box>
      </MuiDrawer>
    );
  }
}

function DrawerListItem(props: {
  to: string;
  title: string;
  icon: ReactNode;
  notExact?: boolean;
  hidden?: boolean;
}) {
  if (props.hidden) return null;

  return (
    <NavLink
      to={props.to}
      className="drawer-list-item"
      exact={!props.notExact}
      activeClassName="active"
      hidden={props.hidden}
    >
      <ListItem button key={props.title} className="drawer-list-item">
        <ListItemIcon>
          <div className="icon-wrapper">{props.icon}</div>
        </ListItemIcon>
        {/* The text label remains in DOM, but you can hide it with SCSS when minimized */}
        <Typography variant="body2">{props.title}</Typography>
      </ListItem>
    </NavLink>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user,
});

export default connect(mapStateToProps)(withWidth()(Drawer));
